/**
 * Almacén en memoria del mock de flotas, persistido en localStorage para no
 * perder los cambios al recargar. Guarda las entidades «en bruto» y calcula al
 * vuelo lo que el backend calcularía (estado de la flota, de cada vehículo y
 * conductor, caducidades), con las mismas reglas que el contrato.
 */
import {
  AccountingSettings,
  DriverPresence,
  DriverWeekStats,
  FleetRules,
  SHIFT_SLOTS,
  ShiftSlot,
  VehiclePresence,
  Weekday,
  AuditEntry,
  ChallengeParticipant,
  DemandChannel,
  DispatchPolicy,
  DriverChallenge,
  FLEET_KIND_MODE,
  FleetKind,
  FleetZone,
  GeoPoint,
  PassengerFeeRegion,
  PassengerFeeRule,
  ServiceType,
  StandQueueEntry,
  TaxiStand,
  DocumentReviewDetail,
  DocumentReviewHistoryEntry,
  DocumentReviewItem,
  DocumentStatus,
  DocumentType,
  DocumentsSummary,
  EmploymentType,
  Fleet,
  FleetBilling,
  FleetDocument,
  FleetDriver,
  FleetDriverUser,
  FleetMember,
  FleetMembershipStatus,
  FleetMode,
  FleetNotificationSettings,
  FleetOnboardingStep,
  FleetShifts,
  FleetVehicle,
  FleetVehicleStatus,
  NamedRef,
  ProfessionalDriver,
  ProfessionalDriverOnboardingStatus,
  ProfessionalDriverWorkingAs,
  RejectionReason,
  SettlementMovement,
  ShiftAssignment,
  ShiftSlotHours,
  documentTypesFor,
  expiryStatusFor,
  resolvePassengerFee,
} from 'src/app/models/drivers-fleet';
import {
  FLEET_MOCK_STATE_KEY,
  FleetMockSettings,
  readFleetMockSettings,
} from './fleet-mock.config';

// ------------------------------------------------------------------ tipos del almacén

export interface MockDocument extends FleetDocument {
  mode: FleetMode;
  fleetId: string | null;
  /** Conductor dueño (documentos de conductor y vehículos de licencia propia). */
  driverUserId: string | null;
  /** Hay una versión más nueva del mismo documento. */
  superseded: boolean;
  history: DocumentReviewHistoryEntry[];
}

export interface MockFleet {
  id: string;
  name: string;
  kind: FleetKind;
  mode: FleetMode;
  regionId: string;
  contactEmail: string;
  fleetCode: string;
  billing: FleetBilling | null;
  billingStatus: DocumentStatus;
  billingRejection: { reason: RejectionReason; comment: string | null } | null;
  legalAcceptance: Fleet['legalAcceptance'];
  submittedAt: string | null;
  approvedAt: string | null;
  createdAt: string;
  defaultRevenueSharePercentage: number;
  /** Flota del usuario del escenario (la del titular). */
  isScenarioFleet: boolean;
  // Mockup 28
  ibanMasked: string | null;
  rules: FleetRules;
  accounting: AccountingSettings;
}

export interface MockVehicle {
  id: string;
  fleetId: string | null;
  /** Vehículo de un conductor con licencia propia. */
  ownerUserId: string | null;
  plate: string;
  licenseNumber: string;
  brand: string | null;
  model: string | null;
  seats: number | null;
  createdAt: string;
  // Mockup 28
  year: number | null;
  odometerKm: number | null;
  municipality: string | null;
  /** `null`: sin enviar a revisión. */
  submittedAt: string | null;
}

export interface MockProDriver {
  user: FleetDriverUser;
  mode: FleetMode;
  regionId: string;
  ownLicense: boolean;
  billing: FleetBilling | null;
  billingStatus: DocumentStatus;
  createdAt: string;
}

export interface MockMembership {
  membershipId: string;
  fleetId: string;
  userId: string;
  status: FleetMembershipStatus;
  employmentType: EmploymentType | null;
  revenueSharePercentage: number | null;
  requestedAt: string;
  acceptedAt: string | null;
  removedAt: string | null;
  /** Mockup 28: desactivado por la flota y titular que también conduce. */
  active: boolean;
  isOwner: boolean;
}

export interface MockState {
  version: number;
  seededFor: string;
  regions: { id: string; name: string; shortName: string }[];
  fleets: MockFleet[];
  vehicles: MockVehicle[];
  drivers: MockProDriver[];
  memberships: MockMembership[];
  documents: MockDocument[];
  members: (FleetMember & { fleetId: string })[];
  shifts: Record<string, FleetShifts>;
  shiftSlots: Record<string, ShiftSlotHours[]>;
  notifications: Record<string, FleetNotificationSettings>;
  movements: Record<string, SettlementMovement[]>;
  // Fase 4a
  feeRules: PassengerFeeRule[];
  feeRegions: PassengerFeeRegion[];
  feeHistory: AuditEntry[];
  challenges: DriverChallenge[];
  /** Participantes por reto (progreso simulado). */
  challengeParticipants: Record<string, ChallengeParticipant[]>;
  stands: (Omit<TaxiStand, 'queueCount'> & { fleetId: string })[];
  standQueues: Record<string, StandQueueEntry[]>;
  zones: (FleetZone & { fleetId: string })[];
  dispatchPolicies: Record<string, DispatchPolicy>;
  policyHistory: Record<string, AuditEntry[]>;
  counter: number;
}

const STATE_VERSION = 3;
export const MOCK_REVIEWER: NamedRef = { id: '00000000-0000-4000-8000-0000000000aa', name: 'Revisor WAIIS (mock)' };

// ------------------------------------------------------------------ utilidades

/** Generador determinista (mulberry32) para que la siembra salga siempre igual. */
function prng(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function nowIso(): string {
  return new Date().toISOString();
}

function daysFromToday(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function isoDaysAgo(days: number, hour = 10): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  d.setHours(hour, 0, 0, 0);
  return d.toISOString();
}

/** Imagen de relleno para documentos (los ficheros de verdad no salen del navegador). */
export function placeholderFile(label: string): FleetDocument['file'] {
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="840" height="594" viewBox="0 0 840 594">` +
    `<rect width="840" height="594" fill="#f4f2f7"/><rect x="24" y="24" width="792" height="546" rx="18" fill="#fff" stroke="#d9d4e3" stroke-width="2"/>` +
    `<text x="420" y="270" font-family="Inter,Arial,sans-serif" font-size="30" font-weight="700" fill="#2b2240" text-anchor="middle">${label}</text>` +
    `<text x="420" y="320" font-family="Inter,Arial,sans-serif" font-size="20" fill="#7a7189" text-anchor="middle">Documento simulado (mock)</text></svg>`;
  return {
    url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
    urlExpiresAt: null,
    mimeType: 'image/svg+xml',
    sizeBytes: svg.length,
    originalName: `${label.toLowerCase().replace(/\s+/g, '-')}.svg`,
  };
}

const DOC_LABEL: Record<DocumentType, string> = {
  bank_ownership_certificate: 'Certificado bancario',
  route_sheet_authorization: 'Apoderamiento hojas de ruta',
  circulation_permit: 'Permiso de circulación',
  technical_sheet_itv: 'Ficha técnica e ITV',
  insurance_policy: 'Póliza del seguro',
  taxi_municipal_license: 'Licencia municipal de taxi',
  vtc_transport_card: 'Tarjeta de transporte VTC',
  identity: 'DNI',
  driving_license: 'Carnet de conducir',
  taxi_driver_card: 'Carnet de autotaxi',
  vtc_driver_permit: 'Permiso de conductor VTC',
  social_security_registration: 'Alta en la Seguridad Social',
};

/** Ficheros subidos en esta sesión (blob: URL); tras recargar se ven con el relleno. */
const sessionFiles = new Map<string, FleetDocument['file']>();

export function rememberUploadedFile(documentId: string, file: File): FleetDocument['file'] {
  const stored = {
    url: URL.createObjectURL(file),
    urlExpiresAt: null,
    mimeType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    originalName: file.name,
  };
  sessionFiles.set(documentId, stored);
  return stored;
}

export function fileFor(doc: MockDocument): FleetDocument['file'] {
  if (doc.status === 'pending') {
    return null;
  }
  return sessionFiles.get(doc.id) ?? placeholderFile(DOC_LABEL[doc.type]);
}

// ------------------------------------------------------------------ almacén

/** Lo que decide los datos sembrados: el escenario y el modo (no el rol). */
function seedKey(settings: FleetMockSettings): string {
  return JSON.stringify({ scenario: settings.scenario, kind: settings.kind });
}

export class FleetMockStore {
  state: MockState;
  settings: FleetMockSettings;

  constructor(reseed = false) {
    this.settings = readFleetMockSettings();
    // El rol solo cambia los permisos, no los datos: no obliga a volver a sembrar.
    const seededFor = seedKey(this.settings);
    const saved = reseed ? null : this.load();
    this.state = saved && saved.seededFor === seededFor ? saved : seed(this.settings);
    this.save();
  }

  private load(): MockState | null {
    try {
      const raw = localStorage.getItem(FLEET_MOCK_STATE_KEY);
      const parsed = raw ? (JSON.parse(raw) as MockState) : null;
      return parsed?.version === STATE_VERSION ? parsed : null;
    } catch {
      return null;
    }
  }

  save(): void {
    try {
      localStorage.setItem(FLEET_MOCK_STATE_KEY, JSON.stringify(this.state));
    } catch {
      // localStorage lleno: el mock sigue en memoria hasta recargar.
    }
  }

  newId(): string {
    this.state.counter += 1;
    const n = this.state.counter.toString(16).padStart(12, '0');
    return `00000000-0000-4000-8000-${n}`;
  }

  scenarioFleet(): MockFleet | undefined {
    return this.state.fleets.find((f) => f.isScenarioFleet);
  }

  fleet(fleetId: string): MockFleet | undefined {
    return this.state.fleets.find((f) => f.id === fleetId);
  }

  region(regionId: string | null): NamedRef | null {
    const region = this.state.regions.find((r) => r.id === regionId);
    return region ? { id: region.id, name: region.name } : null;
  }

  // ---------------------------------------------------------------- documentos

  /** Documento vigente (última versión) de un dueño y tipo. */
  currentDocument(ownerType: MockDocument['ownerType'], ownerId: string, type: DocumentType): MockDocument | undefined {
    return this.state.documents.find(
      (d) => d.ownerType === ownerType && d.ownerId === ownerId && d.type === type && !d.superseded,
    );
  }

  currentDocuments(ownerType: MockDocument['ownerType'], ownerId: string): MockDocument[] {
    return this.state.documents.filter((d) => d.ownerType === ownerType && d.ownerId === ownerId && !d.superseded);
  }

  /** Documento vigente de un tipo; si no existe, uno `pending` (sin subir). */
  documentOrPending(
    ownerType: MockDocument['ownerType'],
    ownerId: string,
    type: DocumentType,
    context: { mode: FleetMode; fleetId: string | null; driverUserId: string | null },
  ): MockDocument {
    return (
      this.currentDocument(ownerType, ownerId, type) ??
      pendingDocument(`pending-${ownerType}-${ownerId}-${type}`, ownerType, ownerId, type, context)
    );
  }

  /** Crea una versión nueva (en revisión) y deja la anterior como sustituida. */
  addDocumentVersion(
    ownerType: MockDocument['ownerType'],
    ownerId: string,
    type: DocumentType,
    context: { mode: FleetMode; fleetId: string | null; driverUserId: string | null },
    uploader: { id: string; name: string; kind: 'fleet' | 'driver' },
    file: File | null,
  ): MockDocument {
    const previous = this.currentDocument(ownerType, ownerId, type);
    if (previous) {
      previous.superseded = true;
    }
    const id = this.newId();
    const at = nowIso();
    const doc: MockDocument = {
      ...pendingDocument(id, ownerType, ownerId, type, context),
      status: 'in_review',
      uploadedAt: at,
      uploadedBy: uploader,
      version: (previous?.version ?? 0) + 1,
      history: [{ action: 'uploaded', actor: { id: uploader.id, name: uploader.name }, at, reason: null, comment: null }],
    };
    if (file) {
      rememberUploadedFile(id, file);
    }
    this.state.documents.push(doc);
    return doc;
  }

  /** Documento tal como lo ve la flota: sin revisor concreto y sin fichero de conductor. */
  documentForFleet(doc: MockDocument): FleetDocument {
    return {
      ...publicDocument(doc),
      file: doc.ownerType === 'driver' ? null : fileFor(doc),
      reviewedBy: doc.reviewedAt ? { name: 'Equipo WAIIS' } : null,
    };
  }

  documentForAdmin(doc: MockDocument, withFile: boolean): FleetDocument {
    return { ...publicDocument(doc), file: withFile ? fileFor(doc) : null };
  }

  // ---------------------------------------------------------------- flota

  fleetSteps(fleet: MockFleet): FleetOnboardingStep[] {
    const steps: FleetOnboardingStep[] = [
      {
        key: 'billing_info',
        status: fleet.billing ? fleet.billingStatus : 'pending',
        rejectionReason: fleet.billingRejection?.reason ?? null,
        rejectionComment: fleet.billingRejection?.comment ?? null,
        updatedAt: fleet.submittedAt,
      },
    ];
    for (const type of documentTypesFor('fleet', fleet.mode)) {
      const doc = this.currentDocument('fleet', fleet.id, type);
      steps.push({
        key: type as FleetOnboardingStep['key'],
        status: doc?.status ?? 'pending',
        documentId: doc?.id ?? null,
        rejectionReason: doc?.rejectionReason ?? null,
        rejectionComment: doc?.rejectionComment ?? null,
        updatedAt: doc?.reviewedAt ?? doc?.uploadedAt ?? null,
      });
    }
    return steps;
  }

  fleetStatus(fleet: MockFleet): Fleet['status'] {
    if (!fleet.submittedAt) {
      return 'draft';
    }
    const steps = this.fleetSteps(fleet);
    if (steps.some((s) => s.status === 'rejected')) {
      return 'rejected';
    }
    return steps.every((s) => s.status === 'approved') ? 'approved' : 'in_review';
  }

  fleetVehicles(fleetId: string): MockVehicle[] {
    return this.state.vehicles.filter((v) => v.fleetId === fleetId);
  }

  fleetMemberships(fleetId: string, statuses?: FleetMembershipStatus[]): MockMembership[] {
    return this.state.memberships.filter(
      (m) => m.fleetId === fleetId && (!statuses || statuses.includes(m.status)),
    );
  }

  // ---------------------------------------------------------------- mockup 28: en vivo, turnos y semana

  /** Día (1 = lunes) y franja de ahora; la noche de madrugada cuenta como la del día anterior. */
  nowSlot(now: Date = new Date()): { weekday: Weekday; slot: ShiftSlot } {
    const hour = now.getHours();
    const jsDay = now.getDay() || 7;
    if (hour < 6) return { weekday: (((jsDay + 5) % 7) + 1) as Weekday, slot: 'night' };
    return { weekday: jsDay as Weekday, slot: hour < 14 ? 'morning' : hour < 22 ? 'afternoon' : 'night' };
  }

  private assignmentsOf(fleetId: string) {
    return this.state.shifts[fleetId]?.assignments ?? [];
  }

  /**
   * Conectado si tiene turno ahora o, fuera de turno, uno de cada tres (para que
   * el mapa del mock no salga vacío en fin de semana). En viaje o libre cambia
   * cada 2 minutos. Solo con la flota aprobada y el conductor activo.
   */
  driverPresence(membership: MockMembership, now: Date = new Date()): DriverPresence {
    const fleet = this.fleet(membership.fleetId);
    if (!fleet || this.fleetStatus(fleet) !== 'approved' || !membership.active || membership.status !== 'accepted') return 'offline';
    const { weekday, slot } = this.nowSlot(now);
    const onShift = this.assignmentsOf(fleet.id).some((a) => a.membershipId === membership.membershipId && a.weekday === weekday && a.slot === slot);
    const day = now.toISOString().slice(0, 10);
    if (!onShift && hashUnit(membership.membershipId + day) > 0.35) return 'offline';
    const bucket = Math.floor(now.getTime() / 120_000);
    return hashUnit(membership.membershipId + bucket) < 0.45 ? 'on_trip' : 'available';
  }

  vehiclePresence(vehicle: MockVehicle, now: Date = new Date()): VehiclePresence {
    if (!vehicle.fleetId) return 'parked';
    const { weekday, slot } = this.nowSlot(now);
    const driverNow = this.assignmentsOf(vehicle.fleetId).find((a) => a.vehicleId === vehicle.id && a.weekday === weekday && a.slot === slot);
    const membership = driverNow && this.state.memberships.find((m) => m.membershipId === driverNow.membershipId);
    return membership && this.driverPresence(membership, now) !== 'offline' ? 'on_street' : 'parked';
  }

  /** Coche del turno de ahora o, si no, el primero que tiene asignado. */
  driverVehicle(membership: MockMembership, now: Date = new Date()): { id: string; plate: string } | null {
    const mine = this.assignmentsOf(membership.fleetId).filter((a) => a.membershipId === membership.membershipId);
    if (!mine.length) return null;
    const { weekday, slot } = this.nowSlot(now);
    const current = mine.find((a) => a.weekday === weekday && a.slot === slot) ?? mine[0];
    const vehicle = this.state.vehicles.find((v) => v.id === current.vehicleId);
    return vehicle ? { id: vehicle.id, plate: vehicle.plate } : null;
  }

  driverShiftLabel(membership: MockMembership): string | null {
    const slots = new Set(this.assignmentsOf(membership.fleetId).filter((a) => a.membershipId === membership.membershipId).map((a) => a.slot));
    if (!slots.size) return null;
    return SHIFT_SLOTS.filter((slot) => slots.has(slot)).map((slot) => SLOT_LABEL[slot]).join(', ');
  }

  /** Conductores de un coche por sus turnos: «Mañana 6–14, L–V». */
  vehicleDrivers(vehicle: MockVehicle): { membershipId: string; name: string; shiftLabel: string }[] {
    const hours = Object.fromEntries((this.state.shiftSlots[vehicle.fleetId!] ?? []).map((s) => [s.slot, `${+s.start.slice(0, 2)}–${+s.end.slice(0, 2)}`]));
    const groups = new Map<string, { membershipId: string; slot: ShiftSlot; days: number[] }>();
    for (const a of this.assignmentsOf(vehicle.fleetId!).filter((x) => x.vehicleId === vehicle.id)) {
      const key = `${a.membershipId}|${a.slot}`;
      const group = groups.get(key) ?? { membershipId: a.membershipId, slot: a.slot, days: [] };
      group.days.push(a.weekday);
      groups.set(key, group);
    }
    return [...groups.values()].map((g) => {
      const membership = this.state.memberships.find((m) => m.membershipId === g.membershipId);
      const user = membership ? this.driver(membership.userId)?.user : undefined;
      return {
        membershipId: g.membershipId,
        name: user ? `${user.name} ${user.surname}` : '—',
        shiftLabel: `${SLOT_LABEL[g.slot]} ${hours[g.slot] ?? ''}, ${dayRangeLabel(g.days.sort())}`.replace(' ,', ','),
      };
    });
  }

  /** Lunes de la semana de `date` (YYYY-MM-DD, hora local). */
  static weekStart(date: Date = new Date()): Date {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return d;
  }

  weekStats(membership: MockMembership, now: Date = new Date()): DriverWeekStats | null {
    const from = FleetMockStore.weekStart(now).getTime();
    const trips = (this.state.movements[membership.fleetId] ?? []).filter(
      (m) => m.type === 'trip_earning' && m.driver?.membershipId === membership.membershipId && Date.parse(m.occurredAt) >= from,
    );
    if (!trips.length) return null;
    const unit = hashUnit(membership.membershipId);
    return {
      trips: trips.length,
      hoursOnline: Math.round(trips.length * (1.4 + unit) * 10) / 10,
      fareCents: trips.reduce((sum, m) => sum + m.amountCents, 0),
      rating: Math.round((4.3 + unit * 0.7) * 10) / 10,
      acceptanceRate: Math.round((0.82 + unit * 0.17) * 100) / 100,
      cancellationRate: Math.round((0.01 + (1 - unit) * 0.08) * 1000) / 1000,
    };
  }

  /** Documentos caducados, rechazados o que caducan pronto (aviso del menú). */
  documentAlerts(fleet: MockFleet): number {
    const docs = [
      ...this.currentDocuments('fleet', fleet.id),
      ...this.fleetVehicles(fleet.id).flatMap((v) => this.vehicleDocuments(v, fleet.mode)),
      ...this.fleetMemberships(fleet.id, ['accepted']).flatMap((m) => this.driverDocuments(this.driver(m.userId)!)),
    ];
    return docs.filter((d) => {
      if (d.status === 'rejected') return true;
      if (d.status !== 'approved') return false;
      const expiry = expiryStatusFor(d.expiresAt);
      return expiry === 'expired' || expiry === 'expiring_soon';
    }).length;
  }

  toFleet(fleet: MockFleet, withBilling = true): Fleet {
    const memberships = this.fleetMemberships(fleet.id);
    const accepted = memberships.filter((m) => m.status === 'accepted');
    const vehicles = this.fleetVehicles(fleet.id);
    const shifts = this.state.shifts[fleet.id];
    return {
      id: fleet.id,
      name: fleet.name,
      kind: fleet.kind,
      mode: fleet.mode,
      status: this.fleetStatus(fleet),
      region: this.region(fleet.regionId),
      contactEmail: fleet.contactEmail,
      fleetCode: fleet.fleetCode,
      ...(withBilling
        ? { billing: fleet.billing, legalAcceptance: fleet.legalAcceptance, bankAccount: { ibanMasked: fleet.ibanMasked }, rules: { ...fleet.rules } }
        : {}),
      onboardingSteps: this.fleetSteps(fleet),
      gettingStarted: {
        hasVehicle: vehicles.length > 0,
        hasAcceptedDriver: accepted.length > 0,
        hasShifts: (shifts?.assignments.length ?? 0) > 0,
      },
      counts: {
        vehicles: vehicles.length,
        driversAccepted: accepted.length,
        driverRequestsPending: memberships.filter((m) => m.status === 'pending').length,
        documentAlerts: this.documentAlerts(fleet),
      },
      submittedAt: fleet.submittedAt,
      approvedAt: this.fleetStatus(fleet) === 'approved' ? fleet.approvedAt ?? fleet.submittedAt : null,
      createdAt: fleet.createdAt,
    };
  }

  // ---------------------------------------------------------------- vehículos

  vehicleDocuments(vehicle: MockVehicle, mode: FleetMode): MockDocument[] {
    return documentTypesFor('vehicle', mode).map((type) =>
      this.documentOrPending('vehicle', vehicle.id, type, {
        mode,
        fleetId: vehicle.fleetId,
        driverUserId: vehicle.ownerUserId,
      }),
    );
  }

  vehicleStatus(docs: MockDocument[]): FleetVehicleStatus {
    if (docs.some((d) => d.status === 'pending')) {
      return 'incomplete';
    }
    if (docs.some((d) => d.status === 'rejected')) {
      return 'rejected';
    }
    return docs.every((d) => d.status === 'approved') ? 'approved' : 'in_review';
  }

  toVehicle(vehicle: MockVehicle, mode: FleetMode): FleetVehicle {
    const docs = this.vehicleDocuments(vehicle, mode);
    const status = this.vehicleStatus(docs);
    return {
      id: vehicle.id,
      plate: vehicle.plate,
      licenseNumber: vehicle.licenseNumber,
      brand: vehicle.brand,
      model: vehicle.model,
      year: vehicle.year,
      seats: vehicle.seats,
      odometerKm: vehicle.odometerKm,
      municipality: vehicle.municipality,
      // Sin enviar a revisión sigue «incompleto» aunque tenga todo subido.
      status: vehicle.submittedAt || !vehicle.fleetId ? status : 'incomplete',
      canWork: status === 'approved' && !!(vehicle.submittedAt || !vehicle.fleetId) && !docs.some((d) => expiryStatusFor(d.expiresAt) === 'expired'),
      presence: vehicle.fleetId ? this.vehiclePresence(vehicle) : null,
      drivers: vehicle.fleetId ? this.vehicleDrivers(vehicle) : [],
      documents: docs.map((d) => this.documentForFleet(d)),
      submittedAt: vehicle.submittedAt,
      createdAt: vehicle.createdAt,
    };
  }

  // ---------------------------------------------------------------- conductores

  driver(userId: string): MockProDriver | undefined {
    return this.state.drivers.find((d) => d.user.id === userId);
  }

  /** Fleet membership activa (aceptada) de un conductor, si la tiene. */
  activeMembership(userId: string): MockMembership | undefined {
    return this.state.memberships.find((m) => m.userId === userId && m.status === 'accepted');
  }

  workingAs(driver: MockProDriver): ProfessionalDriverWorkingAs {
    const membership = this.activeMembership(driver.user.id);
    if (driver.ownLicense || !membership) {
      return 'own_license';
    }
    return membership.employmentType === 'salaried' ? 'fleet_salaried' : 'fleet_self_employed';
  }

  /** Documentos que se piden al conductor según su modo y cómo trabaja (DDEV-5). */
  driverDocumentTypes(driver: MockProDriver): DocumentType[] {
    const types: DocumentType[] = ['identity', 'driving_license'];
    types.push(driver.mode === 'taxi' ? 'taxi_driver_card' : 'vtc_driver_permit');
    if (driver.mode === 'vtc' && this.workingAs(driver) === 'fleet_salaried') {
      types.push('social_security_registration');
    }
    return types;
  }

  driverDocuments(driver: MockProDriver): MockDocument[] {
    const membership = this.activeMembership(driver.user.id);
    return this.driverDocumentTypes(driver).map((type) =>
      this.documentOrPending('driver', driver.user.id, type, {
        mode: driver.mode,
        fleetId: membership?.fleetId ?? null,
        driverUserId: driver.user.id,
      }),
    );
  }

  documentsSummary(docs: MockDocument[]): DocumentsSummary {
    const summary: DocumentsSummary = { approved: 0, inReview: 0, rejected: 0, pending: 0, expired: 0, expiringSoon: 0 };
    for (const doc of docs) {
      const expiry = expiryStatusFor(doc.expiresAt);
      if (doc.status === 'approved' && expiry === 'expired') {
        summary.expired += 1;
        continue;
      }
      if (doc.status === 'approved') {
        summary.approved += 1;
        if (expiry === 'expiring_soon') {
          summary.expiringSoon += 1;
        }
      } else if (doc.status === 'in_review') {
        summary.inReview += 1;
      } else if (doc.status === 'rejected') {
        summary.rejected += 1;
      } else {
        summary.pending += 1;
      }
    }
    return summary;
  }

  driverDocsOk(driver: MockProDriver): boolean {
    const summary = this.documentsSummary(this.driverDocuments(driver));
    return summary.approved > 0 && summary.inReview + summary.rejected + summary.pending + summary.expired === 0;
  }

  toFleetDriver(membership: MockMembership): FleetDriver {
    const driver = this.driver(membership.userId)!;
    const fleet = this.fleet(membership.fleetId);
    const docs = this.driverDocuments(driver);
    return {
      membershipId: membership.membershipId,
      user: driver.user,
      status: membership.status,
      employmentType: membership.employmentType,
      revenueSharePercentage: membership.revenueSharePercentage,
      documentsSummary: this.documentsSummary(docs),
      canWork:
        membership.status === 'accepted' &&
        membership.active &&
        !!fleet &&
        this.fleetStatus(fleet) === 'approved' &&
        this.driverDocsOk(driver),
      active: membership.active,
      isOwner: membership.isOwner,
      presence: membership.status === 'accepted' ? this.driverPresence(membership) : null,
      vehicle: membership.status === 'accepted' ? this.driverVehicle(membership) : null,
      shiftLabel: membership.status === 'accepted' ? this.driverShiftLabel(membership) : null,
      weekStats: membership.status === 'accepted' ? this.weekStats(membership) : null,
      requestedAt: membership.requestedAt,
      acceptedAt: membership.acceptedAt,
    };
  }

  /** Vehículo de un conductor con licencia propia. */
  ownVehicle(userId: string): MockVehicle | undefined {
    return this.state.vehicles.find((v) => v.ownerUserId === userId);
  }

  driverOnboardingStatus(driver: MockProDriver, docs: MockDocument[]): ProfessionalDriverOnboardingStatus {
    const statuses = [...docs.map((d) => d.status), ...(driver.ownLicense ? [driver.billing ? driver.billingStatus : 'pending'] : [])];
    if (statuses.some((s) => s === 'rejected')) {
      return 'rejected';
    }
    if (statuses.some((s) => s === 'pending')) {
      return 'in_progress';
    }
    return statuses.every((s) => s === 'approved') ? 'approved' : 'in_review';
  }

  toProfessionalDriver(driver: MockProDriver): ProfessionalDriver {
    const membership = this.activeMembership(driver.user.id);
    const fleet = membership ? this.fleet(membership.fleetId) : undefined;
    const vehicle = driver.ownLicense ? this.ownVehicle(driver.user.id) : undefined;
    const docs = [
      ...this.driverDocuments(driver),
      ...(vehicle ? this.vehicleDocuments(vehicle, driver.mode) : []),
    ];
    const onboardingStatus = this.driverOnboardingStatus(driver, docs);
    return {
      user: driver.user,
      mode: driver.mode,
      workingAs: this.workingAs(driver),
      region: this.region(driver.regionId),
      onboardingStatus,
      steps: [
        ...docs.map((d) => ({ key: d.type, status: d.status })),
        ...(driver.ownLicense
          ? [{ key: 'billing_info' as const, status: driver.billing ? driver.billingStatus : ('pending' as DocumentStatus) }]
          : []),
      ],
      fleet: fleet ? { id: fleet.id, name: fleet.name } : null,
      canWork: onboardingStatus === 'approved' && (!fleet || this.fleetStatus(fleet) === 'approved'),
      createdAt: driver.createdAt,
    };
  }

  // ---------------------------------------------------------------- admin: verificación

  reviewItem(doc: MockDocument): DocumentReviewItem {
    const fleet = doc.fleetId ? this.fleet(doc.fleetId) : undefined;
    const driver = doc.driverUserId ? this.driver(doc.driverUserId) : undefined;
    return {
      document: this.documentForAdmin(doc, false),
      mode: doc.mode,
      owner: { type: doc.ownerType, id: doc.ownerId, label: this.ownerLabel(doc) },
      fleet: fleet ? { id: fleet.id, name: fleet.name } : null,
      driver: driver && doc.ownerType !== 'fleet' && (doc.ownerType === 'driver' || !fleet)
        ? { id: driver.user.id, name: `${driver.user.name} ${driver.user.surname}` }
        : null,
    };
  }

  reviewDetail(doc: MockDocument): DocumentReviewDetail {
    const item = this.reviewItem(doc);
    const driver = item.driver ? this.driver(item.driver.id) : undefined;
    const identity = driver ? this.currentDocument('driver', driver.user.id, 'identity') : undefined;
    return {
      ...item,
      document: this.documentForAdmin(doc, true),
      driver: item.driver
        ? {
            ...item.driver,
            identityCheck: identity
              ? { status: identity.status, checkedAt: identity.reviewedAt, provider: 'tecalis' }
              : null,
          }
        : null,
      previousVersions: this.state.documents
        .filter((d) => d.ownerType === doc.ownerType && d.ownerId === doc.ownerId && d.type === doc.type && d.id !== doc.id)
        .sort((a, b) => b.version - a.version)
        .map((d) => ({
          id: d.id,
          version: d.version,
          status: d.status,
          rejectionReason: d.rejectionReason,
          uploadedAt: d.uploadedAt ?? d.reviewedAt ?? '',
        })),
      history: [...doc.history].reverse(),
    };
  }

  ownerLabel(doc: MockDocument): string {
    if (doc.ownerType === 'fleet') {
      return this.fleet(doc.ownerId)?.name ?? '—';
    }
    if (doc.ownerType === 'vehicle') {
      return this.state.vehicles.find((v) => v.id === doc.ownerId)?.plate ?? '—';
    }
    const driver = this.driver(doc.ownerId);
    return driver ? `${driver.user.name} ${driver.user.surname}` : '—';
  }
}

// ------------------------------------------------------------------ documentos (puros)

function pendingDocument(
  id: string,
  ownerType: MockDocument['ownerType'],
  ownerId: string,
  type: DocumentType,
  context: { mode: FleetMode; fleetId: string | null; driverUserId: string | null },
): MockDocument {
  return {
    id,
    ownerType,
    ownerId,
    type,
    status: 'pending',
    expiresAt: null,
    expiryStatus: 'none',
    licenseNumber: null,
    rejectionReason: null,
    rejectionComment: null,
    file: null,
    uploadedAt: null,
    uploadedBy: null,
    reviewedAt: null,
    reviewedBy: null,
    version: 0,
    mode: context.mode,
    fleetId: context.fleetId,
    driverUserId: context.driverUserId,
    superseded: false,
    history: [],
  };
}

/** Quita los campos internos del almacén y recalcula la caducidad. */
function publicDocument(doc: MockDocument): FleetDocument {
  const { mode, fleetId, driverUserId, superseded, history, ...rest } = doc;
  return { ...rest, expiryStatus: expiryStatusFor(doc.expiresAt) };
}

// ------------------------------------------------------------------ siembra

interface SeedDocSpec {
  type: DocumentType;
  status: DocumentStatus;
  expiresInDays?: number | null;
  reason?: RejectionReason;
  comment?: string;
  uploadedDaysAgo?: number;
  licenseNumber?: string;
}

const REGIONS = [
  { id: 'r-manresa', name: 'Manresa', shortName: 'MAN' },
  { id: 'r-barcelona', name: 'Barcelona', shortName: 'BCN' },
  { id: 'r-sabadell', name: 'Sabadell', shortName: 'SBD' },
  { id: 'r-igualada', name: 'Igualada', shortName: 'IGD' },
  { id: 'r-zaragoza', name: 'Zaragoza', shortName: 'ZGZ' },
];

const FIRST_NAMES = ['Jordi', 'Marta', 'Pau', 'Laia', 'Sergi', 'Núria', 'Àlex', 'Montse', 'Joan', 'Carla', 'David', 'Anna', 'Xavier', 'Rosa'];
const SURNAMES = ['Soler', 'Puig', 'Vila', 'Ribas', 'Ferrer', 'Roca', 'Serra', 'Font', 'Casals', 'Pons', 'Mas', 'Vidal'];
const CARS: [string, string][] = [
  ['Toyota', 'Corolla Hybrid'],
  ['Toyota', 'C-HR'],
  ['Skoda', 'Octavia'],
  ['Hyundai', 'Ioniq'],
  ['Kia', 'Niro'],
  ['Mercedes', 'Clase E'],
  ['Tesla', 'Model 3'],
];

function seed(settings: FleetMockSettings): MockState {
  const rand = prng(20261009);
  const pick = <T>(list: readonly T[]): T => list[Math.floor(rand() * list.length)];
  const state: MockState = {
    version: STATE_VERSION,
    seededFor: seedKey(settings),
    regions: REGIONS,
    fleets: [],
    vehicles: [],
    drivers: [],
    memberships: [],
    documents: [],
    members: [],
    shifts: {},
    shiftSlots: {},
    notifications: {},
    movements: {},
    feeRules: [],
    feeRegions: [],
    feeHistory: [],
    challenges: [],
    challengeParticipants: {},
    stands: [],
    standQueues: {},
    zones: [],
    dispatchPolicies: {},
    policyHistory: {},
    counter: 0,
  };
  const id = (): string => {
    state.counter += 1;
    return `00000000-0000-4000-8000-${state.counter.toString(16).padStart(12, '0')}`;
  };
  seedPassengerFees(state, id);
  seedChallenges(state, id);

  const addDoc = (
    ownerType: MockDocument['ownerType'],
    ownerId: string,
    context: { mode: FleetMode; fleetId: string | null; driverUserId: string | null },
    spec: SeedDocSpec,
    uploader: { id: string; name: string; kind: 'fleet' | 'driver' },
  ): void => {
    if (spec.status === 'pending') {
      return;
    }
    const uploadedAt = isoDaysAgo(spec.uploadedDaysAgo ?? 3);
    const reviewed = spec.status === 'approved' || spec.status === 'rejected';
    const reviewedAt = reviewed ? isoDaysAgo(Math.max((spec.uploadedDaysAgo ?? 3) - 1, 0), 12) : null;
    const history: DocumentReviewHistoryEntry[] = [
      { action: 'uploaded', actor: { id: uploader.id, name: uploader.name }, at: uploadedAt, reason: null, comment: null },
    ];
    if (reviewed) {
      history.push({
        action: spec.status === 'approved' ? 'approved' : 'rejected',
        actor: MOCK_REVIEWER,
        at: reviewedAt!,
        reason: spec.reason ?? null,
        comment: spec.comment ?? null,
      });
    }
    // Un rechazado viene de una versión anterior también rechazada, para ver el historial.
    state.documents.push({
      ...pendingDocument(id(), ownerType, ownerId, spec.type, context),
      status: spec.status,
      expiresAt:
        spec.status === 'approved' && spec.expiresInDays !== null && spec.expiresInDays !== undefined
          ? daysFromToday(spec.expiresInDays)
          : null,
      licenseNumber: spec.status === 'approved' ? spec.licenseNumber ?? null : null,
      rejectionReason: spec.status === 'rejected' ? spec.reason ?? 'illegible' : null,
      rejectionComment: spec.status === 'rejected' ? spec.comment ?? null : null,
      uploadedAt,
      uploadedBy: uploader,
      reviewedAt,
      reviewedBy: reviewed ? MOCK_REVIEWER : null,
      version: 1,
      history,
    });
  };

  const makeUser = (n: number): FleetDriverUser => {
    const name = FIRST_NAMES[n % FIRST_NAMES.length];
    const surname = SURNAMES[(n * 7) % SURNAMES.length];
    return {
      id: id(),
      name,
      surname,
      email: `${name}.${surname}.${n}@example.com`.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, ''),
      phone: `+34 6${String(10000000 + Math.floor(rand() * 89999999)).slice(0, 8)}`,
      selfieUrl: null,
    };
  };

  const plate = (): string => {
    const letters = 'BCDFGHJKLMNPRSTVWXYZ';
    const digits = String(1000 + Math.floor(rand() * 8999));
    return `${digits} ${pick(letters.split(''))}${pick(letters.split(''))}${pick(letters.split(''))}`;
  };

  const fleetCode = (name: string): string =>
    `${name.toUpperCase().normalize('NFD').replace(/[^A-Z]/g, '').slice(-6)}-${1000 + Math.floor(rand() * 8999)}`;

  const defaultSlots: ShiftSlotHours[] = [
    { slot: 'morning', start: '06:00', end: '14:00' },
    { slot: 'afternoon', start: '14:00', end: '22:00' },
    { slot: 'night', start: '22:00', end: '06:00' },
  ];

  /** Siembra una flota con sus vehículos, conductores, turnos y movimientos. */
  const addFleet = (seedOptions: {
    name: string;
    kind: FleetKind;
    regionId: string;
    submitted: boolean;
    billing: DocumentStatus | 'none';
    companyDocs: SeedDocSpec[];
    vehicles: SeedDocSpec[][];
    acceptedDrivers: number;
    pendingDrivers: number;
    driverDocs: (index: number) => SeedDocSpec[];
    withShifts: boolean;
    withMovements: boolean;
    isScenarioFleet?: boolean;
  }): MockFleet => {
    const options = { ...seedOptions, mode: FLEET_KIND_MODE[seedOptions.kind] };
    const dispatchCenter = options.kind === 'dispatch_center';
    const fleetId = id();
    const createdDaysAgo = 40 + Math.floor(rand() * 60);
    const fleet: MockFleet = {
      id: fleetId,
      name: options.name,
      kind: options.kind,
      mode: options.mode,
      regionId: options.regionId,
      contactEmail: `flota@${options.name.toLowerCase().normalize('NFD').replace(/[^a-z]/g, '')}.es`,
      fleetCode: fleetCode(options.name),
      billing:
        options.billing === 'none'
          ? null
          : {
              entityType: 'company',
              legalName: `${options.name} SL`,
              taxId: `B${String(60000000 + Math.floor(rand() * 9999999))}`,
              address: { line: 'Carrer Major 12', postalCode: '08241', city: 'Manresa', province: 'Barcelona', country: 'ES' },
              vatRegime: 'general',
            },
      billingStatus: options.billing === 'none' ? 'pending' : options.billing,
      billingRejection: null,
      legalAcceptance: options.submitted
        ? { fleetTermsVersion: '2026-10-09', selfBillingAgreementVersion: '2026-10-09', acceptedAt: isoDaysAgo(createdDaysAgo - 1) }
        : null,
      submittedAt: options.submitted ? isoDaysAgo(createdDaysAgo - 1) : null,
      approvedAt: options.submitted ? isoDaysAgo(createdDaysAgo - 3) : null,
      createdAt: isoDaysAgo(createdDaysAgo),
      defaultRevenueSharePercentage: 70,
      isScenarioFleet: !!options.isScenarioFleet,
      ibanMasked: options.billing === 'none' ? null : `ES21 •••• ${String(1000 + Math.floor(rand() * 8999))}`,
      rules: { expiryReminders: true, blockOnExpiry: true, acceptCash: false, cancellationAlert: true },
      accounting: { monthlyEmailEnabled: false, email: null },
    };
    state.fleets.push(fleet);
    state.shiftSlots[fleetId] = defaultSlots.map((s) => ({ ...s }));
    state.notifications[fleetId] = { documentExpiring: true, documentRejected: true, driverRequests: true, weeklySettlement: false };
    const owner = { id: id(), name: `Titular de ${options.name}`, kind: 'fleet' as const };
    state.members.push(
      { id: id(), fleetId, email: fleet.contactEmail, name: owner.name, user: { id: owner.id, name: owner.name }, role: 'fleet_owner', status: 'active', invitedAt: fleet.createdAt, lastSeenAt: new Date().toISOString() },
      { id: id(), fleetId, email: `contable@${fleet.contactEmail.split('@')[1]}`, name: 'Gestoria Puig', user: { id: id(), name: 'Gestoria Puig' }, role: 'fleet_accountant', status: 'active', invitedAt: isoDaysAgo(20), lastSeenAt: isoDaysAgo(3, 9) },
      { id: id(), fleetId, email: `gestion@${fleet.contactEmail.split('@')[1]}`, name: 'Pau Riera', user: null, role: 'fleet_manager', status: 'invited', invitedAt: isoDaysAgo(2), lastSeenAt: null },
    );

    const context = { mode: options.mode, fleetId, driverUserId: null };
    for (const spec of options.companyDocs) {
      if (spec.type === 'route_sheet_authorization' && options.mode !== 'vtc') {
        continue;
      }
      addDoc('fleet', fleetId, context, spec, owner);
    }

    const vehicleIds: string[] = [];
    options.vehicles.forEach((docs) => {
      const [brand, model] = pick(CARS);
      const vehicle: MockVehicle = {
        id: id(),
        fleetId,
        ownerUserId: null,
        plate: plate(),
        licenseNumber: `${options.mode === 'taxi' ? 'LIC' : 'VTC'}-${String(100 + Math.floor(rand() * 899))}`,
        brand,
        model,
        seats: 4,
        createdAt: isoDaysAgo(20),
        year: 2019 + Math.floor(rand() * 6),
        odometerKm: 12000 + Math.floor(rand() * 150000),
        municipality: options.mode === 'taxi' ? (REGIONS.find((r) => r.id === options.regionId)?.name ?? null) : null,
        // Los vehículos sembrados con sus 4 documentos ya se enviaron; los demás, «incompletos».
        submittedAt: docs.length >= 4 ? isoDaysAgo(19) : null,
      };
      state.vehicles.push(vehicle);
      vehicleIds.push(vehicle.id);
      for (const spec of docs) {
        const type = mapVehicleType(spec.type, options.mode);
        addDoc('vehicle', vehicle.id, context, { ...spec, type, licenseNumber: vehicle.licenseNumber }, owner);
      }
    });

    const membershipIds: string[] = [];
    const totalDrivers = options.acceptedDrivers + options.pendingDrivers;
    for (let i = 0; i < totalDrivers; i++) {
      const user = makeUser(state.drivers.length + 3);
      const accepted = i < options.acceptedDrivers;
      const salaried = accepted && !dispatchCenter && i % 3 === 2;
      state.drivers.push({
        user,
        mode: options.mode,
        regionId: options.regionId,
        ownLicense: false,
        billing: null,
        billingStatus: 'pending',
        createdAt: isoDaysAgo(25 - i),
      });
      const membership: MockMembership = {
        membershipId: id(),
        fleetId,
        userId: user.id,
        status: accepted ? 'accepted' : 'pending',
        employmentType: accepted ? (dispatchCenter ? 'associate' : salaried ? 'salaried' : 'self_employed') : null,
        revenueSharePercentage: accepted && !salaried && !dispatchCenter ? 70 : null,
        requestedAt: isoDaysAgo(accepted ? 18 - i : 1),
        acceptedAt: accepted ? isoDaysAgo(17 - i) : null,
        removedAt: null,
        active: true,
        // El titular también conduce en la flota del escenario («Tú»).
        isOwner: !!options.isScenarioFleet && accepted && i === 0,
      };
      state.memberships.push(membership);
      if (accepted) {
        membershipIds.push(membership.membershipId);
      }
      const driverContext = { mode: options.mode, fleetId: accepted ? fleetId : null, driverUserId: user.id };
      const uploader = { id: user.id, name: `${user.name} ${user.surname}`, kind: 'driver' as const };
      for (const spec of options.driverDocs(i)) {
        const type = mapDriverType(spec.type, options.mode);
        if (type === 'social_security_registration' && !(options.mode === 'vtc' && salaried)) {
          continue;
        }
        addDoc('driver', user.id, driverContext, { ...spec, type }, uploader);
      }
    }

    // Turnos: cada conductor aceptado en un coche y franja, de lunes a viernes.
    const assignments: ShiftAssignment[] = [];
    if (options.withShifts && !dispatchCenter && vehicleIds.length && membershipIds.length) {
      membershipIds.forEach((membershipId, index) => {
        const vehicleId = vehicleIds[index % vehicleIds.length];
        const slot = (['morning', 'afternoon', 'night'] as const)[Math.floor(index / vehicleIds.length) % 3];
        for (const weekday of [1, 2, 3, 4, 5] as const) {
          assignments.push({ vehicleId, weekday, slot, membershipId });
        }
      });
    }
    state.shifts[fleetId] = { assignments, updatedAt: assignments.length ? isoDaysAgo(5) : null };

    // Movimientos de liquidación: viajes de las últimas semanas con su desglose.
    const movements: SettlementMovement[] = [];
    const payoutDays: { day: number; share: number }[] = [];
    if (options.withMovements) {
      for (let day = 0; day < 28; day++) {
        const trips = 2 + Math.floor(rand() * 4);
        for (let t = 0; t < trips; t++) {
          const membershipId = pick(membershipIds);
          const membership = state.memberships.find((m) => m.membershipId === membershipId)!;
          const driverUser = state.drivers.find((d) => d.user.id === membership.userId)!.user;
          const fareCents = 800 + Math.floor(rand() * 5200);
          const description = pick(['Manresa → Barcelona', 'Manresa → Aeropuerto', 'Sant Joan → Manresa', 'Manresa centro', 'Igualada → Manresa']);
          const demandChannel = pickWeighted(rand, CHANNEL_WEIGHTS);
          const serviceType: ServiceType = description.includes('Aeropuerto') ? 'airport_premium' : rand() < 0.2 ? 'reservation' : 'immediate';
          const fee = resolvePassengerFee(state.feeRules, regionsEnabled(state), {
            regionId: options.regionId, mode: options.mode, demandChannel, serviceType, fareCents,
          });
          const breakdown = { fareCents, demandChannel, serviceType, feeCents: fee.feeCents, passengerTotalCents: fee.passengerTotalCents };
          const at = isoDaysAgo(day, 7 + Math.floor(rand() * 15));
          const tripId = id();
          const driverRef = { membershipId, name: `${driverUser.name} ${driverUser.surname}` };
          movements.push({
            id: id(),
            occurredAt: at,
            type: 'trip_earning',
            amountCents: fareCents,
            trip: { id: tripId, ...breakdown },
            driver: driverRef,
            description,
          });
          if (membership.revenueSharePercentage) {
            movements.push({
              id: id(),
              occurredAt: at,
              type: 'driver_share',
              amountCents: -Math.round((fareCents * membership.revenueSharePercentage) / 100),
              trip: { id: tripId, ...breakdown },
              driver: driverRef,
              description: null,
            });
          }
        }
        if (day % 7 === 3 && !dispatchCenter) payoutDays.push({ day, share: 0.6 + rand() * 0.3 });
      }
      // Pagos semanales a la cuenta de la flota: parte del saldo de ese momento,
      // en orden cronológico para que el saldo nunca quede en negativo.
      for (const payout of payoutDays.sort((a, b) => b.day - a.day)) {
        const occurredAt = isoDaysAgo(payout.day, 8);
        const balance = movements.filter((m) => m.occurredAt < occurredAt).reduce((total, m) => total + m.amountCents, 0);
        const amountCents = -Math.floor(balance * payout.share);
        if (amountCents < 0) movements.push({ id: id(), occurredAt, type: 'payout', amountCents, trip: null, driver: null, description: 'Pago semanal' });
      }
    }
    state.movements[fleetId] = movements.sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
    if (dispatchCenter) seedDispatchCenter(state, fleetId, options.regionId, membershipIds, id, rand);
    return fleet;
  };

  const approvedVehicle = (expiresInDays = 300): SeedDocSpec[] => [
    { type: 'circulation_permit', status: 'approved', uploadedDaysAgo: 30 },
    { type: 'technical_sheet_itv', status: 'approved', expiresInDays, uploadedDaysAgo: 30 },
    { type: 'insurance_policy', status: 'approved', expiresInDays: expiresInDays + 40, uploadedDaysAgo: 30 },
    { type: 'taxi_municipal_license', status: 'approved', expiresInDays: 700, uploadedDaysAgo: 30 },
  ];
  const reviewVehicle = (): SeedDocSpec[] => [
    { type: 'circulation_permit', status: 'in_review', uploadedDaysAgo: 1 },
    { type: 'technical_sheet_itv', status: 'in_review', uploadedDaysAgo: 1 },
    { type: 'insurance_policy', status: 'in_review', uploadedDaysAgo: 2 },
    { type: 'taxi_municipal_license', status: 'in_review', uploadedDaysAgo: 2 },
  ];
  const approvedDriver = (): SeedDocSpec[] => [
    { type: 'identity', status: 'approved', expiresInDays: 1500, uploadedDaysAgo: 20 },
    { type: 'driving_license', status: 'approved', expiresInDays: 2000, uploadedDaysAgo: 20 },
    { type: 'taxi_driver_card', status: 'approved', expiresInDays: 900, uploadedDaysAgo: 20 },
    { type: 'social_security_registration', status: 'approved', uploadedDaysAgo: 20 },
  ];
  const reviewDriver = (): SeedDocSpec[] => [
    { type: 'identity', status: 'approved', expiresInDays: 1500, uploadedDaysAgo: 2 },
    { type: 'driving_license', status: 'in_review', uploadedDaysAgo: 1 },
    { type: 'taxi_driver_card', status: 'in_review', uploadedDaysAgo: 1 },
    { type: 'social_security_registration', status: 'in_review', uploadedDaysAgo: 1 },
  ];
  const companyApproved: SeedDocSpec[] = [
    { type: 'bank_ownership_certificate', status: 'approved', uploadedDaysAgo: 50 },
    { type: 'route_sheet_authorization', status: 'approved', uploadedDaysAgo: 50 },
  ];
  const companyReview: SeedDocSpec[] = [
    { type: 'bank_ownership_certificate', status: 'in_review', uploadedDaysAgo: 1 },
    { type: 'route_sheet_authorization', status: 'in_review', uploadedDaysAgo: 1 },
  ];

  // ---- Flotas de ejemplo para admin (siempre).
  addFleet({
    name: 'Taxis Manresa', kind: 'owner_operator', regionId: 'r-manresa', submitted: true, billing: 'approved',
    companyDocs: companyApproved,
    vehicles: [approvedVehicle(), approvedVehicle(18), approvedVehicle(), approvedVehicle(-4)],
    acceptedDrivers: 5, pendingDrivers: 1,
    driverDocs: (i) => (i < 5 ? approvedDriver() : reviewDriver()),
    withShifts: true, withMovements: true,
  });
  addFleet({
    name: 'VTC Bages', kind: 'vtc_fleet', regionId: 'r-manresa', submitted: true, billing: 'in_review',
    companyDocs: companyReview,
    vehicles: [reviewVehicle(), reviewVehicle()],
    acceptedDrivers: 2, pendingDrivers: 1,
    driverDocs: () => reviewDriver(),
    withShifts: false, withMovements: false,
  });
  addFleet({
    name: 'Radio Taxi Igualada', kind: 'dispatch_center', regionId: 'r-igualada', submitted: true, billing: 'approved',
    companyDocs: [{ type: 'bank_ownership_certificate', status: 'rejected', reason: 'holder_mismatch', comment: 'El titular del certificado es una persona física, no la empresa.', uploadedDaysAgo: 4 }],
    vehicles: [[...reviewVehicle().slice(0, 2), { type: 'insurance_policy', status: 'rejected', reason: 'expired', uploadedDaysAgo: 4 }, { type: 'taxi_municipal_license', status: 'in_review', uploadedDaysAgo: 4 }]],
    acceptedDrivers: 1, pendingDrivers: 0,
    driverDocs: () => reviewDriver(),
    withShifts: false, withMovements: false,
  });

  // ---- Conductores con licencia propia (DDEV-5), para la cola de admin.
  for (let i = 0; i < 4; i++) {
    const mode: FleetMode = i % 2 === 0 ? 'taxi' : 'vtc';
    const user = makeUser(40 + i);
    state.drivers.push({
      user,
      mode,
      regionId: pick(REGIONS).id,
      ownLicense: true,
      billing: {
        entityType: 'self_employed',
        legalName: `${user.name} ${user.surname}`,
        taxId: `${String(30000000 + Math.floor(rand() * 9999999))}K`,
        address: { line: 'Avinguda Catalunya 4', postalCode: '08240', city: 'Manresa', province: 'Barcelona', country: 'ES' },
        vatRegime: 'simplified',
      },
      billingStatus: i === 3 ? 'approved' : 'in_review',
      createdAt: isoDaysAgo(6 - i),
    });
    const [brand, model] = pick(CARS);
    const vehicle: MockVehicle = {
      id: id(), fleetId: null, ownerUserId: user.id, plate: plate(),
      licenseNumber: `${mode === 'taxi' ? 'LIC' : 'VTC'}-${String(100 + Math.floor(rand() * 899))}`,
      brand, model, seats: 4, createdAt: isoDaysAgo(6 - i),
      year: 2020 + i, odometerKm: 40000 + i * 21000, municipality: mode === 'taxi' ? 'Manresa' : null, submittedAt: isoDaysAgo(6 - i),
    };
    state.vehicles.push(vehicle);
    const context = { mode, fleetId: null, driverUserId: user.id };
    const uploader = { id: user.id, name: `${user.name} ${user.surname}`, kind: 'driver' as const };
    const status: DocumentStatus = i === 3 ? 'approved' : 'in_review';
    for (const spec of [...reviewDriver().slice(0, 3)]) {
      addDoc('driver', user.id, context, { ...spec, type: mapDriverType(spec.type, mode), status: spec.type === 'identity' ? 'approved' : status, expiresInDays: spec.type === 'identity' ? 1500 : 800 }, uploader);
    }
    for (const spec of reviewVehicle()) {
      addDoc('vehicle', vehicle.id, context, { ...spec, type: mapVehicleType(spec.type, mode), status: i === 2 && spec.type === 'insurance_policy' ? 'rejected' : status, reason: 'illegible', expiresInDays: 365 }, uploader);
    }
  }

  // ---- Flota del titular según el escenario de DDEV-6.
  if (settings.scenario) {
    const kind = settings.kind;
    const name = kind === 'dispatch_center' ? 'Radio Taxi Vallès' : kind === 'vtc_fleet' ? 'Vallès VTC' : 'Taxis Vallès';
    const vehicleDocs = (spec: SeedDocSpec[]): SeedDocSpec[] => spec;
    switch (settings.scenario) {
      case 'alta':
        addFleet({ name, kind, regionId: 'r-sabadell', submitted: false, billing: 'none', companyDocs: [], vehicles: [], acceptedDrivers: 0, pendingDrivers: 0, driverDocs: () => [], withShifts: false, withMovements: false, isScenarioFleet: true });
        break;
      case 'panel-vacio':
        addFleet({ name, kind, regionId: 'r-sabadell', submitted: true, billing: 'in_review', companyDocs: companyReview, vehicles: [], acceptedDrivers: 0, pendingDrivers: 0, driverDocs: () => [], withShifts: false, withMovements: false, isScenarioFleet: true });
        break;
      case 'con-altas':
        addFleet({ name, kind, regionId: 'r-sabadell', submitted: true, billing: 'in_review', companyDocs: companyReview, vehicles: [reviewVehicle(), vehicleDocs(reviewVehicle().slice(0, 2))], acceptedDrivers: 1, pendingDrivers: 2, driverDocs: () => reviewDriver(), withShifts: false, withMovements: false, isScenarioFleet: true });
        break;
      case 'rechazo': {
        const fleet = addFleet({
          name, kind, regionId: 'r-sabadell', submitted: true, billing: 'approved',
          companyDocs: [
            { type: 'bank_ownership_certificate', status: 'rejected', reason: 'holder_mismatch', comment: 'El IBAN no está a nombre de la empresa.', uploadedDaysAgo: 3 },
            { type: 'route_sheet_authorization', status: 'approved', uploadedDaysAgo: 3 },
          ],
          vehicles: [[...approvedVehicle().slice(0, 2), { type: 'insurance_policy', status: 'rejected', reason: 'illegible', comment: 'La foto está movida.', uploadedDaysAgo: 3 }, { type: 'taxi_municipal_license', status: 'approved', expiresInDays: 700, uploadedDaysAgo: 3 }], reviewVehicle()],
          acceptedDrivers: 1, pendingDrivers: 1, driverDocs: () => reviewDriver(), withShifts: false, withMovements: false, isScenarioFleet: true,
        });
        fleet.approvedAt = null;
        break;
      }
      case 'aprobada':
        addFleet({
          name, kind, regionId: 'r-sabadell', submitted: true, billing: 'approved', companyDocs: companyApproved,
          vehicles: [approvedVehicle(), approvedVehicle(12), approvedVehicle(), approvedVehicle(-6)],
          acceptedDrivers: 6, pendingDrivers: 2,
          driverDocs: (i) => (i === 1 ? [...approvedDriver().slice(0, 2), { type: 'taxi_driver_card', status: 'approved', expiresInDays: 9, uploadedDaysAgo: 300 }] : i < 6 ? approvedDriver() : reviewDriver()),
          withShifts: true, withMovements: true, isScenarioFleet: true,
        });
        break;
    }
  }

  return state;
}

/** Las semillas usan tipos de taxi; en VTC se cambian por su equivalente. */
function mapVehicleType(type: DocumentType, mode: FleetMode): DocumentType {
  if (mode === 'vtc' && type === 'taxi_municipal_license') {
    return 'vtc_transport_card';
  }
  if (mode === 'taxi' && type === 'vtc_transport_card') {
    return 'taxi_municipal_license';
  }
  return type;
}

function mapDriverType(type: DocumentType, mode: FleetMode): DocumentType {
  if (mode === 'vtc' && type === 'taxi_driver_card') {
    return 'vtc_driver_permit';
  }
  if (mode === 'taxi' && type === 'vtc_driver_permit') {
    return 'taxi_driver_card';
  }
  return type;
}

// ------------------------------------------------------------------ fase 4a: cuota, retos y emisora

const CHANNEL_WEIGHTS: [DemandChannel, number][] = [
  ['waiis_app', 50],
  ['emitter_phone', 25],
  ['stand', 10],
  ['street_hail', 10],
  ['emitter_web', 5],
];

function pickWeighted<T>(rand: () => number, weights: [T, number][]): T {
  const total = weights.reduce((sum, [, w]) => sum + w, 0);
  let r = rand() * total;
  for (const [value, w] of weights) {
    if ((r -= w) < 0) return value;
  }
  return weights[weights.length - 1][0];
}

/** Interruptor de la cuota por región (`regionId → fees_enabled`). */
export function regionsEnabled(state: MockState): Record<string, boolean> {
  return Object.fromEntries(state.feeRegions.map((r) => [r.region.id, r.feesEnabled]));
}

const MOCK_ADMIN: NamedRef = { id: '00000000-0000-4000-8000-0000000000ab', name: 'Ana López (mock)' };

/** Matriz del Master Plan: 0,99 / 1,99 / 2,49 € y piloto a 0 € (salvo Sabadell, para ver desgloses). */
function seedPassengerFees(state: MockState, id: () => string): void {
  const at = isoDaysAgo(12);
  const general: [ServiceType, number][] = [
    ['immediate', 99],
    ['reservation', 199],
    ['airport_premium', 249],
  ];
  general.forEach(([serviceType, amountCents]) =>
    state.feeRules.push({ id: id(), region: null, mode: null, serviceType, amountCents, updatedAt: at }),
  );
  state.feeRules.push({ id: id(), region: { id: 'r-manresa', name: 'Manresa' }, mode: 'taxi', serviceType: 'airport_premium', amountCents: 199, updatedAt: at });
  state.feeRegions = REGIONS.map((region) => {
    const enabled = region.id === 'r-sabadell';
    return {
      region: { id: region.id, name: region.name },
      feesEnabled: enabled,
      changedAt: at,
      changedBy: { name: MOCK_ADMIN.name },
      reason: enabled ? 'Pruebas de la cuota (mock)' : 'Piloto: cuota 0 €',
    };
  });
  state.feeHistory.push({
    id: id(),
    at,
    actor: MOCK_ADMIN,
    action: 'region_toggled',
    target: 'Sabadell',
    changes: [{ field: 'fees_enabled', from: false, to: true }],
  });
}

function seedChallenges(state: MockState, id: () => string): void {
  const day = (offset: number, end = false) => {
    const d = new Date();
    d.setDate(d.getDate() + offset);
    d.setHours(end ? 23 : 0, end ? 59 : 0, end ? 59 : 0, 0);
    return d.toISOString();
  };
  const make = (
    title: string,
    starts: number,
    ends: number,
    goals: DriverChallenge['goals'],
    bonusCents: number,
    extra: Partial<DriverChallenge> = {},
  ): DriverChallenge => ({
    id: id(),
    title,
    description: null,
    region: { id: 'r-manresa', name: 'Manresa' },
    mode: 'taxi',
    startsAt: day(starts),
    endsAt: day(ends, true),
    goals,
    bonusCents,
    status: 'draft',
    counts: { participants: 0, completed: 0 },
    bonusPaidCents: 0,
    createdAt: isoDaysAgo(Math.max(1, -starts + 3)),
    ...extra,
  });
  const goals = (g: Partial<DriverChallenge['goals']>): DriverChallenge['goals'] => ({
    minTrips: null, minHoursOnline: null, minFareCents: null, minRating: null, ...g,
  });
  const active = make('40 viajes este mes', -10, 20, goals({ minTrips: 40, minRating: 4.5 }), 3000, { status: 'active', description: 'Completa 40 viajes con una valoración media de 4,5 o más.' });
  const finished = make('Fin de semana de fiestas', -30, -27, goals({ minHoursOnline: 16 }), 2000, { status: 'finished', region: null, mode: null });
  state.challenges.push(
    active,
    finished,
    make('Madrugadas de noviembre', 15, 45, goals({ minHoursOnline: 30 }), 4000, { status: 'scheduled' }),
    make('Reto aeropuerto (borrador)', 20, 50, goals({ minFareCents: 150000 }), 5000),
  );
  const people = ['Jordi Soler', 'Marta Puig', 'Pau Vila', 'Laia Ribas', 'Sergi Ferrer'];
  state.challengeParticipants[active.id] = people.map((name, i) => ({
    user: { id: id(), name },
    progress: { trips: [44, 31, 22, 12, 40][i], hoursOnline: [80, 61, 40, 18, 72][i], fareCents: [130000, 92000, 70000, 30000, 121000][i], rating: [4.8, 4.6, 4.9, 4.2, 4.7][i] },
    status: i === 0 ? 'paid' : i === 4 ? 'completed' : 'in_progress',
    completedAt: i === 0 || i === 4 ? isoDaysAgo(2) : null,
    bonusPaidAt: i === 0 ? isoDaysAgo(1) : null,
  }));
  state.challengeParticipants[finished.id] = people.slice(0, 3).map((name, i) => ({
    user: { id: id(), name },
    progress: { trips: 12, hoursOnline: [18, 17, 9][i], fareCents: 40000, rating: 4.7 },
    status: i < 2 ? 'paid' : 'in_progress',
    completedAt: i < 2 ? isoDaysAgo(27) : null,
    bonusPaidAt: i < 2 ? isoDaysAgo(26) : null,
  }));
  for (const challenge of state.challenges) {
    const list = state.challengeParticipants[challenge.id] ?? [];
    challenge.counts = { participants: list.length, completed: list.filter((p) => p.status !== 'in_progress').length };
    challenge.bonusPaidCents = list.filter((p) => p.status === 'paid').length * challenge.bonusCents;
  }
}

/** Centro aproximado de cada región para colocar paradas y zonas de ejemplo. */
const REGION_CENTER: Record<string, GeoPoint> = {
  'r-manresa': [41.7287, 1.8236],
  'r-barcelona': [41.3874, 2.1686],
  'r-sabadell': [41.5463, 2.1086],
  'r-igualada': [41.5786, 1.6172],
  'r-zaragoza': [41.6488, -0.8891],
};

function square(center: GeoPoint, dLat: number, dLng: number): GeoPoint[] {
  const [lat, lng] = center;
  return [
    [lat + dLat, lng - dLng],
    [lat + dLat, lng + dLng],
    [lat - dLat, lng + dLng],
    [lat - dLat, lng - dLng],
  ].map(([a, b]) => [Number(a.toFixed(5)), Number(b.toFixed(5))] as GeoPoint);
}

/** Paradas con cola, zonas y política de reparto de una emisora. */
function seedDispatchCenter(
  state: MockState,
  fleetId: string,
  regionId: string,
  membershipIds: string[],
  id: () => string,
  rand: () => number,
): void {
  const [lat, lng] = REGION_CENTER[regionId] ?? REGION_CENTER['r-manresa'];
  const at = isoDaysAgo(8);
  const standSpecs: [string, string, number, number, number | null][] = [
    ['Estació', 'Plaça de l’Estació', 0.0012, -0.0021, 6],
    ['Plaça Major', 'Plaça Major', 0.0028, 0.0006, 4],
    ['Hospital', 'Avinguda de l’Hospital', -0.0105, 0.0042, null],
  ];
  let queued = 0;
  standSpecs.forEach(([name, address, dLat, dLng, capacity], index) => {
    const center: GeoPoint = [Number((lat + dLat).toFixed(5)), Number((lng + dLng).toFixed(5))];
    const standId = id();
    state.stands.push({
      id: standId,
      fleetId,
      name,
      address,
      location: { lat: center[0], lng: center[1] },
      geofence: square(center, 0.00025, 0.00035),
      capacity,
      active: index < 3,
      updatedAt: at,
    });
    // Cada socio está como mucho en una cola: 3, 2 y 1 por parada.
    const queue = membershipIds.slice(queued, queued + 3 - index).map((membershipId, position) => {
      const membership = state.memberships.find((m) => m.membershipId === membershipId)!;
      const user = state.drivers.find((d) => d.user.id === membership.userId)!.user;
      const vehicle = state.vehicles.filter((v) => v.fleetId === fleetId)[position % Math.max(1, state.vehicles.filter((v) => v.fleetId === fleetId).length)];
      return {
        position: position + 1,
        membershipId,
        name: `${user.name} ${user.surname}`,
        plate: vehicle?.plate ?? null,
        joinedAt: new Date(Date.now() - (25 - position * 7) * 60_000).toISOString(),
        consecutiveRejections: position === 1 ? 1 : 0,
        skippedByRequirements: rand() < 0.3 ? 1 : 0,
      };
    });
    queued += queue.length;
    state.standQueues[standId] = queue;
  });
  const zoneSpecs: [string, number, number][] = [
    ['Centre', 0, 0],
    ['Nord', 0.012, 0.002],
    ['Sud', -0.012, -0.002],
  ];
  zoneSpecs.forEach(([name, dLat, dLng]) => {
    state.zones.push({
      id: id(),
      fleetId,
      name,
      polygon: square([lat + dLat, lng + dLng], 0.006, 0.009),
      active: true,
      updatedAt: at,
    });
  });
  state.dispatchPolicies[fleetId] = {
    strategy: 'stand_order',
    intraFleetPriority: true,
    offerTimeoutSeconds: 20,
    standMaxConsecutiveRejections: 2,
    skipByRequirementsKeepsPosition: true,
    updatedAt: at,
    updatedBy: { id: MOCK_ADMIN.id, name: 'Titular (mock)' },
  };
  state.policyHistory[fleetId] = [
    {
      id: id(),
      at,
      actor: { id: MOCK_ADMIN.id, name: 'Titular (mock)' },
      changes: [{ field: 'strategy', from: 'proximity', to: 'stand_order' }],
    },
  ];
}

/** Valor estable 0..1 a partir de un id (para datos derivados del mock). */
export function hashUnit(id: string): number {
  let h = 2166136261;
  for (const char of id) {
    h = Math.imul(h ^ char.charCodeAt(0), 16777619);
  }
  return ((h >>> 0) % 1000) / 1000;
}

const SLOT_LABEL: Record<ShiftSlot, string> = { morning: 'Mañana', afternoon: 'Tarde', night: 'Noche' };
const DAY_SHORT = ['', 'L', 'M', 'X', 'J', 'V', 'S', 'D'];

/** «L–V», «S–D», «Todos los días», «L, X, V». */
export function dayRangeLabel(days: number[]): string {
  const unique = [...new Set(days)].sort();
  if (unique.length === 7) return 'Todos los días';
  const consecutive = unique.length > 1 && unique[unique.length - 1] - unique[0] === unique.length - 1;
  if (consecutive) return `${DAY_SHORT[unique[0]]}–${DAY_SHORT[unique[unique.length - 1]]}`;
  return unique.map((d) => DAY_SHORT[d]).join(', ');
}
